import { Component, OnInit, Input, SimpleChanges, OnDestroy, Output, EventEmitter, input, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MaskType } from "src/app/constants/masks";
import { ListItem } from "src/app/models/listItem";
import { StateTaxDetail, StateTaxExtended } from "src/app/models/taxes";
import { PayrollService } from "src/app/services/payroll.service";
import { HelperService } from "src/app/services/helper.service";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { ChangeRequestStateTax } from "src/app/models/change-requests";
import { BooleanEnum } from "src/app/constants/boolean-enum";
import { LookupService } from "src/app/services/lookup.service";

export type ComponentChange<T, P extends keyof T> = {
    previousValue: T[P];
    currentValue: T[P];
    firstChange: boolean;
};

@Component({
    selector: 'mwp-state-tax-form',
    templateUrl: './state-tax-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class PayrollStateTaxFormComponent implements OnInit, OnDestroy {
    @Input() public title: String;
    @Input() public isEditing: boolean;
    @Input() public hasRequestedEdit: boolean;
    @Input() public stateTax: StateTaxExtended;
    @Input() public changeRequestStateTax: ChangeRequestStateTax;
    @Output() public cancelEmit: EventEmitter<ChangeRequestStateTax> = new EventEmitter<ChangeRequestStateTax>();

    public withholdingStates: ListItem[];
    public stateTaxesForm: FormGroup;
    public maskType = MaskType;
    public stateSelected: string;
    public booleanEnum = BooleanEnum;
    public stateTaxDetail: StateTaxDetail;
    public isUniversalControl: boolean;
    public isArizonaControl: boolean;
    public isNewYorkControl: boolean;
    public effectiveMinDate: Date;

    private exemptStateTaxExemptFieldValue: string = 'exemptStateTaxExemptFieldValue';
    private alternativeCalcID: string = 'alternativeCalcID';
    private azCheckboxExtraWithholding: string = 'azCheckboxExtraWithholding';
    private additionalWithholding: string = 'additionalWithholding';
    private azCheckbox1: string = 'azCheckbox1';
    private azCheckbox2: string = 'azCheckbox2';

    constructor(
        private formBuilder: FormBuilder,
        private payrollService: PayrollService,
        private helperService: HelperService,
        private lookupService: LookupService,
    ) {
    }

    ngOnInit(): void {
        this.effectiveMinDate = new Date();
        this.lookupService.getStatesWithIncomeTax().subscribe((response: ListItem[]) => {
            this.withholdingStates = response;
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes.firstChange && (changes['stateTax'] || changes['changeRequestStateTax'])) {
            this.stateTaxesForm = null;
            this.createStateForm();
        }
    }

    ngOnDestroy(): void {
        this.stateTaxesForm = null;
    }

    private createStateForm(): void {
        if (this.changeRequestStateTax) {
            if (this.changeRequestStateTax.state)
                this.stateSelected = this.helperService.getStateName(this.changeRequestStateTax.state).toUpperCase();

            this.stateTaxDetail = this.changeRequestStateTax.stateTaxDetail;
            this.isUniversalControl = this.changeRequestStateTax.isUniversalControl;
            this.isArizonaControl = this.changeRequestStateTax.isArizonaControl;
            this.isNewYorkControl = this.changeRequestStateTax.isNewYorkControl;

            this.stateTaxesForm = this.formBuilder.group({
                effectiveDate: [{ value: this.changeRequestStateTax.effectiveDate, disabled: !this.isEditing }, Validators.required],
                state: [{ value: this.changeRequestStateTax.state, disabled: !this.isEditing }, Validators.required],
                isExempt: [{ value: this.changeRequestStateTax.isExempt, disabled: !this.isEditing }],
                [this.exemptStateTaxExemptFieldValue]: [{ value: this.changeRequestStateTax.stateTaxDetail.exemptStateTaxExemptFieldValue.toString(), disabled: true }],
                exemptionYearEffective: [{ value: this.changeRequestStateTax.exemptionYearEffective, disabled: !this.isEditing }],
                fullTimeStudent: [{ value: this.changeRequestStateTax.fullTimeStudent, disabled: !this.isEditing }],
                legalResidence: [{ value: this.changeRequestStateTax.legalResidence, disabled: !this.isEditing }],
                spouseLegalResidence: [{ value: this.changeRequestStateTax.spouseLegalResidence, disabled: !this.isEditing }],
                filingStatusID: [{ value: this.changeRequestStateTax.filingStatusID.toString(), disabled: !this.isEditing }],
                [this.alternativeCalcID]: [{ value: this.changeRequestStateTax.alternativeCalcID.toString(), disabled: !this.isEditing }],
                numberOfDependents: [{ value: this.changeRequestStateTax.numberOfDependents, disabled: !this.isEditing }],
                georgiaAdjustmentsAllowance: [{ value: this.changeRequestStateTax.georgiaAdjustmentsAllowance, disabled: !this.isEditing }],
                totalExemptions: [{ value: this.changeRequestStateTax.totalExemptions, disabled: !this.isEditing }],
                additionalAllowance: [{ value: this.changeRequestStateTax.additionalAllowance, disabled: !this.isEditing }],
                totalAllowances: [{ value: this.changeRequestStateTax.totalAllowances, disabled: !this.isEditing }],
                lowIncome: [{ value: this.changeRequestStateTax.lowIncome ? "Y" : "N", disabled: !this.isEditing }],
                [this.additionalWithholding]: [{ value: this.changeRequestStateTax.additionalWithholding, disabled: !this.isEditing }],
                countyAdditionalWithholding: [{ value: this.changeRequestStateTax.countyAdditionalWithholding, disabled: !this.isEditing }],
                reducedWithholding: [{ value: this.changeRequestStateTax.reducedWithholding, disabled: !this.isEditing }],
                countyOfResidence: [{ value: this.changeRequestStateTax.countyOfResidence, disabled: !this.isEditing }],
                countyOfEmployment: [{ value: this.changeRequestStateTax.countyOfEmployment, disabled: !this.isEditing }],
                nonResident: [{ value: this.changeRequestStateTax.nonResident, disabled: !this.isEditing }],
                isBlind: [{ value: this.changeRequestStateTax.isBlind, disabled: !this.isEditing }],
                isSpouseBlind: [{ value: this.changeRequestStateTax.isSpouseBlind, disabled: !this.isEditing }],
                [this.azCheckbox1]: [{ value: this.changeRequestStateTax.stateTaxDetail.azCheckbox1, disabled: !this.isEditing }],
                [this.azCheckboxExtraWithholding]: [{ value: this.changeRequestStateTax.stateTaxDetail.azCheckboxExtraWithholding, disabled: !this.isEditing }],
                [this.azCheckbox2]: [{ value: this.changeRequestStateTax.stateTaxDetail.azCheckbox2, disabled: !this.isEditing }],
                isNYNewYorkCityResident: [{ value: this.changeRequestStateTax.isNYNewYorkCityResident ? "Y" : "N", disabled: !this.isEditing }],
                isNYYonkersResident: [{ value: this.changeRequestStateTax.isNYYonkersResident ? "Y" : "N", disabled: !this.isEditing }],
                nyTotalAllowances: [{ value: this.changeRequestStateTax.nyTotalAllowances, disabled: !this.isEditing }],
                nycAllowances: [{ value: this.changeRequestStateTax.nycAllowances, disabled: !this.isEditing }],
                nycAdditionalWithholding: [{ value: this.changeRequestStateTax.nycAdditionalWithholding, disabled: !this.isEditing }],
                nyYonkersAdditionalWithholding: [{ value: this.changeRequestStateTax.nyYonkersAdditionalWithholding, disabled: !this.isEditing }],
            });

            if (this.isArizonaControl) {
                this.stateTaxesForm.get(this.alternativeCalcID).disable();
                this.stateTaxesForm.get(this.azCheckboxExtraWithholding).disable();
                this.stateTaxesForm.get(this.additionalWithholding).disable();
                this.stateTaxesForm.get(this.azCheckbox1).setValidators(Validators.requiredTrue);
                this.stateTaxesForm.get(this.azCheckbox2).setValidators(Validators.requiredTrue);
                this.stateTaxesForm.updateValueAndValidity();
            }
        } else if (this.stateTax) {
            this.stateSelected = this.helperService.getStateName(this.stateTax.state).toUpperCase();
            this.stateTaxDetail = this.stateTax.stateTaxDetail;

            this.isUniversalControl = this.stateTax.isUniversalControl;
            this.isArizonaControl = this.stateTax.isArizonaControl;
            this.isNewYorkControl = this.stateTax.isNewYorkControl;

            this.stateTaxesForm = this.formBuilder.group({
                effectiveDate: [{ value: this.stateTax.effectiveDate, disabled: !this.isEditing }],
                state: [{ value: this.stateTax.state, disabled: !this.isEditing }],
                isExempt: [{ value: this.stateTax.isExempt, disabled: !this.isEditing }],
                [this.exemptStateTaxExemptFieldValue]: [{ value: this.stateTax.stateTaxDetail.exemptStateTaxExemptFieldValue.toString(), disabled: true }],
                exemptionYearEffective: [{ value: this.stateTax.exemptionYearEffective, disabled: !this.isEditing }],
                fullTimeStudent: [{ value: this.stateTax.fullTimeStudent, disabled: !this.isEditing }],
                legalResidence: [{ value: this.stateTax.legalResidence, disabled: !this.isEditing }],
                spouseLegalResidence: [{ value: this.stateTax.spouseLegalResidence, disabled: !this.isEditing }],
                filingStatusID: [{ value: this.stateTax.filingStatusID.toString(), disabled: !this.isEditing }], // this.stateTax.stateTaxRequiredFields.isFilingStatusRequired - REQUIRED          
                [this.alternativeCalcID]: [{ value: this.stateTax.alternativeCalcID.toString(), disabled: !this.isEditing }],
                numberOfDependents: [{ value: this.stateTax.numberOfDependents, disabled: !this.isEditing }],
                georgiaAdjustmentsAllowance: [{ value: this.stateTax.georgiaAdjustmentsAllowance, disabled: !this.isEditing }],
                totalExemptions: [{ value: this.stateTax.totalExemptions, disabled: !this.isEditing }],
                additionalAllowance: [{ value: this.stateTax.additionalAllowance, disabled: !this.isEditing }],
                totalAllowances: [{ value: this.stateTax.totalAllowances, disabled: !this.isEditing }],
                lowIncome: [{ value: this.stateTax.lowIncome ? "Y" : "N", disabled: !this.isEditing }],
                additionalWithholding: [{ value: this.stateTax.additionalWithholding, disabled: !this.isEditing }],
                countyAdditionalWithholding: [{ value: this.stateTax.countyAdditionalWithholding, disabled: !this.isEditing }],
                reducedWithholding: [{ value: this.stateTax.reducedWithholding, disabled: !this.isEditing }],
                countyOfResidence: [{ value: this.stateTax.countyOfResidence, disabled: !this.isEditing }],
                countyOfEmployment: [{ value: this.stateTax.countyOfEmployment, disabled: !this.isEditing }],
                nonResident: [{ value: this.stateTax.nonResident, disabled: !this.isEditing }],
                isBlind: [{ value: this.stateTax.isBlind, disabled: !this.isEditing }],
                isSpouseBlind: [{ value: this.stateTax.isSpouseBlind, disabled: !this.isEditing }],
                [this.azCheckbox1]: [{ value: this.stateTax.stateTaxDetail.azCheckbox1, disabled: !this.isEditing }],
                [this.azCheckboxExtraWithholding]: [{ value: this.stateTax.stateTaxDetail.azCheckboxExtraWithholding, disabled: !this.isEditing }],
                [this.azCheckbox2]: [{ value: this.stateTax.stateTaxDetail.azCheckbox2, disabled: !this.isEditing }],
                isNYNewYorkCityResident: [{ value: this.stateTax.isNYNewYorkCityResident ? "Y" : "N", disabled: !this.isEditing }],
                isNYYonkersResident: [{ value: this.stateTax.isNYYonkersResident ? "Y" : "N", disabled: !this.isEditing }],
                nyTotalAllowances: [{ value: this.stateTax.nyTotalAllowances, disabled: !this.isEditing }],
                nycAllowances: [{ value: this.stateTax.nycAllowances, disabled: !this.isEditing }],
                nycAdditionalWithholding: [{ value: this.stateTax.nycAdditionalWithholding, disabled: !this.isEditing }],
                nyYonkersAdditionalWithholding: [{ value: this.stateTax.nyYonkersAdditionalWithholding, disabled: !this.isEditing }],
            });
        }
    }

    public stateUpdate(state: string) {
        const l_payload = new StateTaxExtended();
        l_payload.state = state;
        let l_EffectiveDateControl = this.stateTaxesForm.get('effectiveDate');
        const l_EffectiveDate = l_EffectiveDateControl.value ?? '';
        this.payrollService.updateStateForm(l_payload).subscribe((response: StateTaxExtended) => {
            this.stateTax = response;
            this.stateTaxesForm = null;
            this.changeRequestStateTax = null;
            this.createStateForm();
            l_EffectiveDateControl = this.stateTaxesForm.get('effectiveDate');
            l_EffectiveDateControl.setValue(l_EffectiveDate);
            l_EffectiveDateControl.updateValueAndValidity();
        });
    }

    public checkIsExempt(event: MatCheckboxChange): void {
        const l_Control = this.stateTaxesForm.get(this.exemptStateTaxExemptFieldValue);
        l_Control.setValue(null);
        if (event.checked) {
            l_Control.enable();
            l_Control.setValidators(Validators.required);
        } else {
            l_Control.disable();
        }
        l_Control.updateValueAndValidity();
    }

    public checkAZBox1(event: MatCheckboxChange): void {
        this.stateTaxesForm.get(this.azCheckbox1).setValidators(Validators.requiredTrue);
        this.stateTaxesForm.get(this.azCheckbox1).updateValueAndValidity();

        if (event.checked) {
            this.stateTaxesForm.get(this.azCheckbox2).setValue(false);
            this.stateTaxesForm.get(this.azCheckbox2).clearValidators();
            this.stateTaxesForm.get(this.azCheckbox2).updateValueAndValidity();

            this.stateTaxesForm.get(this.alternativeCalcID).setValue(null);
            this.stateTaxesForm.get(this.alternativeCalcID).setValidators(Validators.required);
            this.stateTaxesForm.get(this.alternativeCalcID).enable();
            this.stateTaxesForm.get(this.alternativeCalcID).updateValueAndValidity();

            this.stateTaxesForm.get(this.azCheckboxExtraWithholding).enable();
        } else {
            this.stateTaxesForm.get(this.azCheckbox2).setValidators(Validators.requiredTrue);
            this.stateTaxesForm.get(this.azCheckbox2).updateValueAndValidity();

            this.stateTaxesForm.get(this.alternativeCalcID).setValue(null);
            this.stateTaxesForm.get(this.alternativeCalcID).disable();

            this.stateTaxesForm.get(this.azCheckboxExtraWithholding).setValue(false);
            this.stateTaxesForm.get(this.azCheckboxExtraWithholding).disable();

            this.stateTaxesForm.get(this.additionalWithholding).setValue(0);
            this.stateTaxesForm.get(this.additionalWithholding).disable();
        }

        this.stateTaxesForm.updateValueAndValidity();
    }

    public checkAZBox1AdditonalWitholding(event: MatCheckboxChange): void {
        if (event.checked) {
            this.stateTaxesForm.get(this.additionalWithholding).enable();
        } else {
            this.stateTaxesForm.get(this.additionalWithholding).setValue(0);
            this.stateTaxesForm.get(this.additionalWithholding).disable();
        }

        this.stateTaxesForm.updateValueAndValidity();
    }

    public checkAZBox2(event: MatCheckboxChange): void {
        this.stateTaxesForm.get(this.azCheckbox2).setValidators(Validators.requiredTrue);
        this.stateTaxesForm.get(this.azCheckbox2).updateValueAndValidity();

        if (event.checked) {
            this.stateTaxesForm.get(this.azCheckbox1).setValue(false);
            this.stateTaxesForm.get(this.azCheckbox1).clearValidators();
            this.stateTaxesForm.get(this.azCheckbox1).updateValueAndValidity();

            this.stateTaxesForm.get(this.alternativeCalcID).setValue(0);
            this.stateTaxesForm.get(this.alternativeCalcID).disable();
            this.stateTaxesForm.get(this.azCheckboxExtraWithholding).setValue(false);
            this.stateTaxesForm.get(this.azCheckboxExtraWithholding).disable();
            this.stateTaxesForm.get(this.additionalWithholding).setValue(0);
            this.stateTaxesForm.get(this.additionalWithholding).disable();
            this.stateTaxesForm.updateValueAndValidity();
        } else {
            this.stateTaxesForm.get(this.azCheckbox1).setValidators(Validators.requiredTrue);
            this.stateTaxesForm.get(this.azCheckbox1).updateValueAndValidity();
        }
    }

    public save(): void {
        const l_payload: ChangeRequestStateTax = this.helperService.convertFormGroupIntoObject(this.stateTaxesForm, new ChangeRequestStateTax());
        l_payload.stateTaxDetail = this.helperService.convertFormGroupIntoObject(this.stateTaxesForm, new StateTaxDetail());
        l_payload.lowIncome = this.stateTaxesForm.get('lowIncome').value == 'Y';
        l_payload.isNYNewYorkCityResident = this.stateTaxesForm.get('isNYNewYorkCityResident').value == 'Y';
        l_payload.isNYYonkersResident = this.stateTaxesForm.get('isNYYonkersResident').value == 'Y';

        if (this.changeRequestStateTax) {
            l_payload.id = this.changeRequestStateTax.id;
            l_payload.memberID = this.changeRequestStateTax.memberID;
        } else if (this.stateTax) {
            l_payload.memberID = this.stateTax.memberID;
        }

        this.payrollService.saveStateTaxChange(l_payload).subscribe((response: ChangeRequestStateTax) => {
            this.cancelEmit.emit(l_payload);
        });
    }

    public cancel() {
        this.cancelEmit.emit();
    }
}